import { IntlShape } from "react-intl"
import { Maybe, number, NumberSchema, string, StringSchema } from "yup";

export default class FormValidator {
  constructor(public readonly intl: IntlShape) {}

  public validateString(maxLength = 255): StringSchema<Maybe<string | undefined>> {
    return string()
      .notRequired()
      .max(
        maxLength,
        this.intl.formatMessage({id: 'INPUT.VALIDATION.MAX_LENGTH'}, {max: maxLength})
      )
  }

  public validateStringStrict(maxLength = 255): StringSchema {
    return string()
      .required(this.intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'}))
      .max(
        maxLength,
        this.intl.formatMessage({id: 'INPUT.VALIDATION.MAX_LENGTH'}, {max: maxLength})
      )
  }

  public validateEmail(maxLength = 255): StringSchema<Maybe<string | undefined>> {
    return (
      string()
        .notRequired()
        // .email(this.intl.formatMessage({id: 'INPUT.VALIDATION.EMAIL'}))
        .max(
          maxLength,
          this.intl.formatMessage({id: 'INPUT.VALIDATION.MAX_LENGTH'}, {max: maxLength})
        )
        .matches(
          /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          this.intl.formatMessage({id: 'INPUT.VALIDATION.EMAIL'})
        )
    )
  }

  public validateEmailStrict(maxLength = 255): StringSchema {
    return (
      string()
        .required(this.intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'}))
        // .email(this.intl.formatMessage({id: 'INPUT.VALIDATION.EMAIL'}))
        .max(
          maxLength,
          this.intl.formatMessage({id: 'INPUT.VALIDATION.MAX_LENGTH'}, {max: maxLength})
        )
        .matches(
          /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          this.intl.formatMessage({id: 'INPUT.VALIDATION.EMAIL'})
        )
    )
  }

  public validateInputText(maxLength = 255): StringSchema<Maybe<string | undefined>> {
    return string()
      .notRequired()
      .max(
        maxLength,
        this.intl.formatMessage({id: 'INPUT.VALIDATION.MAX_LENGTH'}, {max: maxLength})
      )
      .when(['metricOptions', 'metricValue'], ([metricOptions, metricValue], schema) => {
        if (metricOptions && metricOptions.length > 0) {
          if (metricValue && metricValue !== '') {
            return schema.required(
              this.intl.formatMessage({
                id: 'INPUT.VALIDATION.METRIC.VALUE_REQUIRED',
              })
            )
          } else {
            return string().test({
              message: this.intl.formatMessage({
                id: 'INPUT.VALIDATION.METRIC.REQUIRED',
              }),
              test: (value) => !value,
            })
          }
        }
        return schema
      })
  }

  public validateInputTextStrict(maxLength = 255): StringSchema {
    return string()
      .max(
        maxLength,
        this.intl.formatMessage({id: 'INPUT.VALIDATION.MAX_LENGTH'}, {max: maxLength})
      )
      .when(
        ['required'],
        ([required], schema) =>
          required && schema.required(this.intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'}))
      )
      .when(['metricOptions', 'metricValue'], ([metricOptions, metricValue], schema) => {
        if (metricOptions && metricOptions.length > 0) {
          if (metricValue && metricValue !== '') {
            return schema.required(
              this.intl.formatMessage({
                id: 'INPUT.VALIDATION.METRIC.VALUE_REQUIRED',
              })
            )
          } else {
            return string()
              .when(
                ['required'],
                ([required], schema) =>
                  required &&
                  schema.required(this.intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'}))
              )
              .test({
                message: this.intl.formatMessage({
                  id: 'INPUT.VALIDATION.METRIC.REQUIRED',
                }),
                test: (value) => !value,
              })
          }
        }
        return schema
      })
  }

  public validateInputNumber(): NumberSchema<Maybe<number | undefined>> {
    return number()
      .notRequired()
      .transform((_value, originalValue) => {
        // Check if originalValue is a string and contains a comma
        if (typeof originalValue === 'string' && originalValue.includes(',')) {
          // Replace the comma with a dot and convert to a number
          return Number(originalValue.replace(',', '.'))
        }
        // Otherwise, return the value as is (Yup will handle standard numbers)
        return Number(originalValue)
      })
      .typeError(this.intl.formatMessage({id: 'INPUT.VALIDATION.NUMBER_TYPE'}))
      .when(['minCheck', 'minValue'], ([minCheck, minValue], schema) =>
        minCheck
          ? schema.min(
              minValue,
              this.intl.formatMessage({id: 'INPUT.VALIDATION.MIN_VALUE'}, {min: minValue})
            )
          : schema
      )
      .when(['maxCheck', 'maxValue'], ([maxCheck, maxValue], schema) =>
        maxCheck
          ? schema.max(
              maxValue,
              this.intl.formatMessage({id: 'INPUT.VALIDATION.MAX_VALUE'}, {max: maxValue})
            )
          : schema
      )
      .when(['metricOptions', 'metricValue'], ([metricOptions, metricValue], schema) => {
        if (metricOptions && metricOptions.length > 0) {
          if (metricValue && metricValue !== '') {
            return schema.required(
              this.intl.formatMessage({
                id: 'INPUT.VALIDATION.METRIC.VALUE_REQUIRED',
              })
            )
          } else {
            return string().test({
              message: this.intl.formatMessage({
                id: 'INPUT.VALIDATION.METRIC.REQUIRED',
              }),
              test: (value) => !value,
            })
          }
        }
        return schema
      })
  }

  public validateInputNumberStrict(): NumberSchema {
    return number()
      .transform((_value, originalValue) => {
        // Check if originalValue is a string and contains a comma
        if (typeof originalValue === 'string' && originalValue.includes(',')) {
          // Replace the comma with a dot and convert to a number
          return Number(originalValue.replace(',', '.'))
        }
        // Otherwise, return the value as is (Yup will handle standard numbers)
        return Number(originalValue)
      })
      .typeError(this.intl.formatMessage({id: 'INPUT.VALIDATION.NUMBER_TYPE'}))
      .when(
        ['required'],
        ([required], schema) =>
          required && schema.required(this.intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'}))
      )
      .when(
        ['minCheck', 'minValue'],
        ([minCheck, minValue], schema) =>
          minCheck &&
          schema.min(
            minValue,
            this.intl.formatMessage({id: 'INPUT.VALIDATION.MIN_VALUE'}, {min: minValue})
          )
      )
      .when(
        ['maxCheck', 'maxValue'],
        ([maxCheck, maxValue], schema) =>
          maxCheck &&
          schema.max(
            maxValue,
            this.intl.formatMessage({id: 'INPUT.VALIDATION.MAX_VALUE'}, {max: maxValue})
          )
      )
      .when(['metricOptions', 'metricValue'], ([metricOptions, metricValue], schema) => {
        if (metricOptions && metricOptions.length > 0) {
          if (metricValue && metricValue !== '') {
            return schema.required(
              this.intl.formatMessage({
                id: 'INPUT.VALIDATION.METRIC.VALUE_REQUIRED',
              })
            )
          } else {
            return string()
              .when(
                ['required'],
                ([required], schema) =>
                  required &&
                  schema.required(this.intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'}))
              )
              .test({
                message: this.intl.formatMessage({
                  id: 'INPUT.VALIDATION.METRIC.REQUIRED',
                }),
                test: (value) => !value,
              })
          }
        }
        return schema
      })
  }

  public validateInputDate(): StringSchema<Maybe<string | undefined>> {
    return string().notRequired()
  }

  public validateInputDateStrict(): StringSchema {
    return string().when(
      ['required'],
      ([required], schema) =>
        required && schema.required(this.intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'}))
    )
  }

  public validateTextArea(maxLength = 3000): StringSchema<Maybe<string | undefined>> {
    return string()
      .notRequired()
      .max(
        maxLength,
        this.intl.formatMessage({id: 'INPUT.VALIDATION.MAX_LENGTH'}, {max: maxLength})
      )
  }

  public validateTextAreaStrict(maxLength = 3000): StringSchema {
    return string()
      .max(
        maxLength,
        this.intl.formatMessage({id: 'INPUT.VALIDATION.MAX_LENGTH'}, {max: maxLength})
      )
      .when(
        ['required'],
        ([required], schema) =>
          required && schema.required(this.intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'}))
      )
  }

  public validateSingleChoice(): StringSchema<Maybe<string | undefined>> {
    return string().notRequired()
  }

  public validateSingleChoiceStrict(): StringSchema {
    return string().when(
      ['required'],
      ([required], schema) =>
        required && schema.required(this.intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'}))
    )
  }
}